import React, { Component } from "react";
import { api, fmt } from "../utils/api";
import toast from "react-hot-toast";
import {
  Download,
  RefreshCw,
  BarChart2,
  TrendingUp,
  Hotel,
  MapPin,
  User,
  Edit2,
} from "lucide-react";

const TAB = [
  { key: "weekly", label: "Weekly Sales", Icon: BarChart2 },
  { key: "individual", label: "Individual Sale", Icon: User },
  { key: "target", label: "Target vs Actual", Icon: TrendingUp },
  { key: "hothotels", label: "Hot Hotels", Icon: Hotel },
  { key: "hotdest", label: "Hot Destinations", Icon: MapPin },
];

const inp = {
  border: "1.5px solid #D1D5DB",
  borderRadius: 10,
  padding: "8px 12px",
  fontFamily: "Sora,sans-serif",
  fontSize: 13,
  color: "#111827",
  outline: "none",
  background: "#fff",
};
const lbl = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#6B7280",
  display: "block",
  marginBottom: 5,
};

// Inline target setter/editor for admin
class SetTargetInline extends React.Component {
  constructor(props) {
    super(props);
    // If existing value passed, start in display mode; otherwise start in edit mode
    this.state = {
      editing: !props.existingValue,
      value: props.existingValue ? String(props.existingValue) : "",
      saving: false,
    };
  }

  async save() {
    const { agentId, month, onSaved } = this.props;
    if (!this.state.value) return;
    this.setState({ saving: true });
    try {
      const api2 = (await import("../utils/api")).api;
      await api2.post("/targets", {
        agent_id: agentId,
        target_month: month,
        target_amount: this.state.value,
      });
      this.setState({ editing: false, saving: false });
      if (onSaved) onSaved();
    } catch (e) {
      toast.error("Failed to save target");
      this.setState({ saving: false });
    }
  }

  render() {
    const { editing, value, saving } = this.state;
    const { existingValue } = this.props;

    // Display mode — shows value + edit pencil button
    if (!editing) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontFamily: "JetBrains Mono,monospace",
              fontSize: 13,
              color: "#374151",
            }}
          >
            ₹{fmt(existingValue)}
          </span>
          <button
            title="Edit target"
            onClick={() =>
              this.setState({ editing: true, value: String(existingValue) })
            }
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 6,
              cursor: "pointer",
              padding: 0,
              color: "#2F5FE0",
            }}
          >
            <Edit2 size={12} />
          </button>
        </div>
      );
    }

    // Edit mode — input + confirm/cancel
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          type="number"
          value={value}
          autoFocus
          onChange={(e) => this.setState({ value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") this.save();
            if (e.key === "Escape")
              this.setState({
                editing: false,
                value: existingValue ? String(existingValue) : "",
              });
          }}
          style={{
            width: 120,
            border: "1.5px solid #2F5FE0",
            borderRadius: 8,
            padding: "4px 8px",
            fontSize: 12,
            outline: "none",
            fontFamily: "JetBrains Mono,monospace",
          }}
          placeholder="₹ amount"
        />
        <button
          onClick={() => this.save()}
          disabled={saving || !value}
          style={{
            background: !value || saving ? "#9CA3AF" : "#0FA968",
            border: "none",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 11,
            color: "#fff",
            cursor: !value || saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "…" : "✓"}
        </button>
        <button
          onClick={() =>
            this.setState({
              editing: false,
              value: existingValue ? String(existingValue) : "",
            })
          }
          style={{
            background: "#F3F4F6",
            border: "1px solid #E5E7EB",
            borderRadius: 6,
            padding: "4px 8px",
            fontSize: 11,
            cursor: "pointer",
            color: "#374151",
          }}
        >
          ✗
        </button>
      </div>
    );
  }
}

class ReportsPage extends Component {
  constructor(props) {
    super(props);
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .slice(0, 10);
    this.state = {
      tab: "weekly",
      from_date: weekAgo,
      to_date: today,
      sold_to: "",
      data: null,
      loading: false,
      downloading: false,
    };
    this.load = this.load.bind(this);
    this.download = this.download.bind(this);
  }

  componentDidMount() {
    this.load();
  }
  componentDidUpdate(_, prev) {
    if (prev.tab !== this.state.tab) this.load();
  }

  async load() {
    this.setState({ loading: true, data: null });
    try {
      const { tab, from_date, to_date, sold_to } = this.state;
      const params = { from_date, to_date };
      if (sold_to) params.sold_to = sold_to;

      const { data: res } = await api.get("/bookings", { params });
      const rows = res.data || [];

      let reportData = null;
      if (tab === "weekly" || tab === "individual") {
        reportData = rows;
      } else if (tab === "hothotels") {
        const map = {};
        rows.forEach((b) => {
          const k = b.hotel_name || "Unknown";
          if (!map[k])
            map[k] = {
              hotel_name: k,
              bookings: 0,
              total_sale: 0,
              total_purchase: 0,
              gross_profit: 0,
            };
          map[k].bookings++;
          map[k].total_sale += parseFloat(b.total_sale) || 0;
          map[k].total_purchase += parseFloat(b.total_purchase) || 0;
          map[k].gross_profit += parseFloat(b.gross_profit) || 0;
        });
        reportData = Object.values(map).sort((a, b) => b.bookings - a.bookings);
      } else if (tab === "hotdest") {
        const map = {};
        rows.forEach((b) => {
          const k = b.destination || "Unknown";
          if (!map[k])
            map[k] = {
              destination: k,
              bookings: 0,
              total_sale: 0,
              gross_profit: 0,
            };
          map[k].bookings++;
          map[k].total_sale += parseFloat(b.total_sale) || 0;
          map[k].gross_profit += parseFloat(b.gross_profit) || 0;
        });
        reportData = Object.values(map).sort((a, b) => b.bookings - a.bookings);
      } else if (tab === "target") {
        const months = [];
        const d = new Date(from_date);
        const end = new Date(to_date);
        while (d <= end) {
          const m = d.toISOString().slice(0, 7);
          if (!months.includes(m)) months.push(m);
          d.setMonth(d.getMonth() + 1);
        }
        const [targetsRes, agentsRes] = await Promise.all([
          api.get("/targets", { params: { month: months[0] } }),
          api.get("/agents"),
        ]);
        const targets = targetsRes.data.data || [];
        const agents = agentsRes.data.data || [];

        // Build branch name → parent agency_name map
        // AND booker name → parent agency_name map
        const branchToAgent = {};
        const bookerToAgent = {};
        await Promise.all(
          agents.map(async (a) => {
            try {
              const res = await api.get(`/agents/${a.id}`);
              const branches = res.data?.data?.branches || [];
              const brokers = res.data?.data?.brokers || [];
              branches.forEach((b) => {
                branchToAgent[b.branch_name.toLowerCase()] = a.agency_name;
              });
              brokers.forEach((bk) => {
                if (bk.name)
                  bookerToAgent[bk.name.toLowerCase()] = a.agency_name;
              });
            } catch (_) {}
          }),
        );

        const agentMap = {};
        agents.forEach((a) => {
          agentMap[a.agency_name] = {
            agency_name: a.agency_name,
            target_amount: 0,
            actual_amount: 0,
            target_id: null,
            agent_id: a.id,
          };
        });
        targets.forEach((t) => {
          if (agentMap[t.agency_name]) {
            agentMap[t.agency_name].target_amount =
              parseFloat(t.target_amount) || 0;
            agentMap[t.agency_name].target_id = t.id;
          }
        });
        // Build bookerMap — keyed by sold_to (booker name / branch name)
        // Each entry knows its parent agent for target lookup
        const bookerMap = {};
        rows.forEach((b) => {
          const soldTo = b.sold_to || "Unknown";
          const key = soldTo.toLowerCase();
          const parentAgent = agentMap[soldTo]
            ? soldTo // direct agency name
            : branchToAgent[key]
              ? branchToAgent[key] // branch → agent
              : bookerToAgent[key]
                ? bookerToAgent[key] // booker → agent
                : null;

          if (!bookerMap[soldTo]) {
            const agent = parentAgent ? agentMap[parentAgent] : null;
            bookerMap[soldTo] = {
              display_name: soldTo,
              parent_agent: parentAgent || "—",
              target_amount: agent?.target_amount || 0,
              agent_id: agent?.agent_id || null,
              actual_amount: 0,
            };
          }
          bookerMap[soldTo].actual_amount += parseFloat(b.total_sale) || 0;
        });

        reportData = {
          rows,
          actual: rows.reduce((s, b) => s + (parseFloat(b.total_sale) || 0), 0),
          agentTargets: Object.values(bookerMap),
          month: months[0],
        };
      }
      this.setState({ data: reportData });
    } catch (e) {
      toast.error("Failed to load report data");
    } finally {
      this.setState({ loading: false });
    }
  }

  async download() {
    this.setState({ downloading: true });
    try {
      const { from_date, to_date } = this.state;
      const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const params = new URLSearchParams({ from_date, to_date });
      const r = await fetch(`${API}/report/download?${params}`);
      if (!r.ok) throw new Error((await r.json()).message || "Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KTS_Report_${from_date}_to_${to_date}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    } finally {
      this.setState({ downloading: false });
    }
  }

  renderWeekly() {
    const raw = this.state.data;
    const rows = Array.isArray(raw) ? raw : raw?.rows || [];
    const total_sale = rows.reduce(
      (s, b) => s + (parseFloat(b.total_sale) || 0),
      0,
    );
    const total_gp = rows.reduce(
      (s, b) => s + (parseFloat(b.gross_profit) || 0),
      0,
    );
    return (
      <div>
        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          {[
            ["Total Bookings", rows.length, "#2F5FE0"],
            ["Total Sale", `₹${fmt(total_sale)}`, "#0FA968"],
            [
              "Gross Profit",
              `₹${fmt(total_gp)}`,
              total_gp >= 0 ? "#0FA968" : "#E1483A",
            ],
          ].map(([l, v, c]) => (
            <div
              key={l}
              style={{
                background: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: 14,
                padding: "16px 20px",
                flex: 1,
                minWidth: 150,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#9CA3AF",
                }}
              >
                {l}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: c,
                  fontFamily: "JetBrains Mono,monospace",
                  marginTop: 6,
                }}
              >
                {v}
              </div>
            </div>
          ))}
        </div>
        {this.renderBookingTable(rows)}
      </div>
    );
  }

  renderBookingTable(rows) {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (!safeRows.length)
      return (
        <p style={{ color: "#9CA3AF", padding: "20px 0" }}>
          No bookings in selected range.
        </p>
      );
    const cols = [
      { key: "id", label: "#", render: (r) => `#${r.id}` },
      {
        key: "booking_date",
        label: "Date",
        render: (r) => String(r.booking_date).slice(0, 10),
      },
      { key: "guest_name", label: "Guest" },
      { key: "hotel_name", label: "Hotel" },
      { key: "destination", label: "Destination" },
      { key: "sold_to", label: "Sold To" },
      { key: "mode", label: "Mode" },
      {
        key: "total_sale",
        label: "Sale",
        render: (r) => `₹${fmt(r.total_sale)}`,
      },
      {
        key: "total_purchase",
        label: "Purchase",
        render: (r) => `₹${fmt(r.total_purchase)}`,
      },
      {
        key: "gross_profit",
        label: "GP",
        render: (r) => {
          const gp = parseFloat(r.gross_profit) || 0;
          return (
            <span
              style={{
                color: gp >= 0 ? "#0FA968" : "#E1483A",
                fontWeight: 600,
              }}
            >
              ₹{fmt(gp)}
            </span>
          );
        },
      },
      { key: "status", label: "Status" },
    ];
    return (
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  style={{
                    padding: "10px 12px",
                    textAlign: "left",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#9CA3AF",
                    borderBottom: "1.5px solid #F3F4F6",
                    background: "#FAFBFF",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeRows.map((r, i) => (
              <tr
                key={r.id}
                style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}
              >
                {cols.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      padding: "10px 12px",
                      fontSize: 12,
                      color: "#374151",
                      borderBottom: "1px solid #F9FAFB",
                      verticalAlign: "middle",
                    }}
                  >
                    {c.render ? c.render(r) : r[c.key] || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderHotHotels() {
    const raw = this.state.data;
    const rows = Array.isArray(raw) ? raw : raw?.rows || [];
    if (!rows.length)
      return <p style={{ color: "#9CA3AF", padding: "20px 0" }}>No data.</p>;
    return (
      <table
        style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}
      >
        <thead>
          <tr>
            {[
              "Rank",
              "Hotel",
              "Bookings",
              "Total Sale",
              "Total Purchase",
              "Gross Profit",
            ].map((h) => (
              <th
                key={h}
                style={{
                  padding: "10px 12px",
                  textAlign: "left",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#9CA3AF",
                  borderBottom: "1.5px solid #F3F4F6",
                  background: "#FAFBFF",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.hotel_name}
              style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}
            >
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: ["#FF6B4A", "#9CA3AF", "#CD7F32"][i] || "#374151",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                #{i + 1}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#111827",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                {r.hotel_name}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  color: "#374151",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                {r.bookings}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  color: "#0FA968",
                  fontWeight: 600,
                  fontFamily: "JetBrains Mono,monospace",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                ₹{fmt(r.total_sale)}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  color: "#374151",
                  fontFamily: "JetBrains Mono,monospace",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                ₹{fmt(r.total_purchase)}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: r.gross_profit >= 0 ? "#0FA968" : "#E1483A",
                  fontFamily: "JetBrains Mono,monospace",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                ₹{fmt(r.gross_profit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  renderHotDest() {
    const raw = this.state.data;
    const rows = Array.isArray(raw) ? raw : raw?.rows || [];
    if (!rows.length)
      return <p style={{ color: "#9CA3AF", padding: "20px 0" }}>No data.</p>;
    return (
      <table
        style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}
      >
        <thead>
          <tr>
            {[
              "Rank",
              "Destination",
              "Bookings",
              "Total Sale",
              "Gross Profit",
            ].map((h) => (
              <th
                key={h}
                style={{
                  padding: "10px 12px",
                  textAlign: "left",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#9CA3AF",
                  borderBottom: "1.5px solid #F3F4F6",
                  background: "#FAFBFF",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.destination}
              style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}
            >
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: ["#FF6B4A", "#9CA3AF", "#CD7F32"][i] || "#374151",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                #{i + 1}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#111827",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                {r.destination}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  color: "#374151",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                {r.bookings}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  color: "#0FA968",
                  fontWeight: 600,
                  fontFamily: "JetBrains Mono,monospace",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                ₹{fmt(r.total_sale)}
              </td>
              <td
                style={{
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: r.gross_profit >= 0 ? "#0FA968" : "#E1483A",
                  fontFamily: "JetBrains Mono,monospace",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                ₹{fmt(r.gross_profit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  renderContent() {
    const { tab, data, loading } = this.state;
    if (loading)
      return (
        <div
          style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}
        >
          <RefreshCw
            size={24}
            style={{ animation: "spin 1s linear infinite", marginBottom: 10 }}
          />
          <p>Loading…</p>
        </div>
      );
    if (!data)
      return <p style={{ color: "#9CA3AF", padding: "20px 0" }}>No data.</p>;
    if (tab === "weekly" || tab === "individual") return this.renderWeekly();
    if (tab === "hothotels") return this.renderHotHotels();
    if (tab === "hotdest") return this.renderHotDest();

    if (tab === "target") {
      const rows = Array.isArray(data) ? data : data?.rows || [];
      const actual = data?.actual || 0;
      const agentTargets = data?.agentTargets || [];
      const month = data?.month || "";
      const { user } = this.props;
      const isAdmin = user?.role_name === "Admin";

      return (
        <div>
          {/* Summary cards */}
          <div
            style={{
              display: "flex",
              gap: 14,
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            {[
              ["Total Actual", `₹${fmt(actual)}`, "#0FA968"],
              ["Total Bookings", rows.length, "#2F5FE0"],
            ].map(([l, v, c]) => (
              <div
                key={l}
                style={{
                  background: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: 12,
                  padding: "14px 18px",
                  flex: 1,
                  minWidth: 140,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#9CA3AF",
                  }}
                >
                  {l}
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: c,
                    marginTop: 5,
                    fontFamily: "JetBrains Mono,monospace",
                  }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>

          {/* Target vs Actual table */}
          {agentTargets.length > 0 && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: 14,
                overflow: "hidden",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  padding: "14px 18px",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                <span
                  style={{ fontSize: 14, fontWeight: 700, color: "#16213E" }}
                >
                  Target vs Actual — {month || "Selected Period"}
                </span>
                {isAdmin && (
                  <span
                    style={{ fontSize: 11, color: "#9CA3AF", marginLeft: 10 }}
                  >
                    Click{" "}
                    <Edit2 size={10} style={{ verticalAlign: "middle" }} /> to
                    edit any target
                  </span>
                )}
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr>
                    {[
                      "Booker / Sold To",
                      "Parent Agent",
                      "Target (₹)",
                      "Actual (₹)",
                      "Achievement",
                      "Status",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 14px",
                          textAlign: "left",
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "#9CA3AF",
                          borderBottom: "1.5px solid #F3F4F6",
                          background: "#FAFBFF",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {agentTargets.map((a, i) => {
                    const pct =
                      a.target_amount > 0
                        ? ((a.actual_amount / a.target_amount) * 100).toFixed(2)
                        : null;
                    const achieved = pct !== null && parseFloat(pct) >= 100;
                    return (
                      <tr
                        key={a.display_name || a.agency_name}
                        style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}
                      >
                        {/* Booker / Sold To */}
                        <td
                          style={{
                            padding: "12px 14px",
                            fontWeight: 700,
                            color: "#111827",
                            fontSize: 13,
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          {a.display_name || a.agency_name}
                        </td>

                        {/* Parent Agent */}
                        <td
                          style={{
                            padding: "12px 14px",
                            fontSize: 12,
                            color: "#6B7280",
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          {a.parent_agent &&
                          a.parent_agent !== a.display_name ? (
                            <span
                              style={{
                                background: "#EFF6FF",
                                color: "#2F5FE0",
                                padding: "2px 8px",
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              🏢 {a.parent_agent}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>

                        {/* Target — editable for admin only if parent agent exists */}
                        <td
                          style={{
                            padding: "12px 14px",
                            fontSize: 13,
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          {isAdmin && a.agent_id ? (
                            <SetTargetInline
                              key={`${a.agent_id}-${month}-${a.target_amount}`}
                              agentId={a.agent_id}
                              month={month}
                              existingValue={
                                a.target_amount > 0 ? a.target_amount : null
                              }
                              onSaved={() => this.load()}
                            />
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "JetBrains Mono,monospace",
                                  color:
                                    a.target_amount > 0 ? "#374151" : "#9CA3AF",
                                  fontStyle:
                                    a.target_amount > 0 ? "normal" : "italic",
                                }}
                              >
                                {a.target_amount > 0
                                  ? `₹${fmt(a.target_amount)}`
                                  : "Not set"}
                              </span>
                              {isAdmin && !a.agent_id && (
                                <span
                                  title="No parent agent — cannot set target"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    background: "#F3F4F6",
                                    border: "1px solid #E5E7EB",
                                    borderRadius: 6,
                                    color: "#D1D5DB",
                                    cursor: "not-allowed",
                                  }}
                                >
                                  <Edit2 size={12} />
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Actual */}
                        <td
                          style={{
                            padding: "12px 14px",
                            fontSize: 13,
                            color: "#0FA968",
                            fontWeight: 600,
                            fontFamily: "JetBrains Mono,monospace",
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          ₹{fmt(a.actual_amount)}
                        </td>

                        {/* Progress bar */}
                        <td
                          style={{
                            padding: "12px 14px",
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          {pct !== null ? (
                            <div>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: achieved ? "#0FA968" : "#D97706",
                                  display: "block",
                                  marginBottom: 4,
                                }}
                              >
                                {pct}%
                              </span>
                              <div
                                style={{
                                  height: 6,
                                  background: "#F3F4F6",
                                  borderRadius: 3,
                                  width: 120,
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    borderRadius: 3,
                                    width: `${Math.min(pct, 100)}%`,
                                    background: achieved
                                      ? "#0FA968"
                                      : "#D97706",
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: "#9CA3AF", fontSize: 12 }}>
                              No target set
                            </span>
                          )}
                        </td>

                        {/* Status badge */}
                        <td
                          style={{
                            padding: "12px 14px",
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          {pct !== null ? (
                            <span
                              style={{
                                padding: "3px 10px",
                                borderRadius: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                background: achieved ? "#E4F9EE" : "#FFFBEB",
                                color: achieved ? "#0B7A4C" : "#92400E",
                                border: `1px solid ${achieved ? "#BEEED9" : "#FDE68A"}`,
                              }}
                            >
                              {achieved ? "✓ Achieved" : "In Progress"}
                            </span>
                          ) : (
                            <span
                              style={{
                                padding: "3px 10px",
                                borderRadius: 20,
                                fontSize: 11,
                                color: "#9CA3AF",
                                background: "#F3F4F6",
                                border: "1px solid #E5E7EB",
                              }}
                            >
                              No Target
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {this.renderBookingTable(rows)}
        </div>
      );
    }
    return null;
  }

  render() {
    const { tab, from_date, to_date, sold_to, downloading } = this.state;
    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: "#16213E",
                letterSpacing: "-0.02em",
              }}
            >
              Reports
            </h1>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>
              Analyse bookings, sales and performance
            </p>
          </div>
          <button
            onClick={this.download}
            disabled={downloading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "11px 22px",
              borderRadius: 12,
              border: "none",
              background: "#FF6B4A",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(255,107,74,0.28)",
            }}
          >
            {downloading ? (
              <>
                <RefreshCw
                  size={14}
                  style={{ animation: "spin 1s linear infinite" }}
                />{" "}
                Generating…
              </>
            ) : (
              <>
                <Download size={14} /> Download Excel
              </>
            )}
          </button>
        </div>

        {/* Filters */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 14,
            padding: "16px 20px",
            marginBottom: 20,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div>
            <label style={lbl}>From Date</label>
            <input
              type="date"
              value={from_date}
              onChange={(e) => this.setState({ from_date: e.target.value })}
              style={inp}
            />
          </div>
          <div>
            <label style={lbl}>To Date</label>
            <input
              type="date"
              value={to_date}
              onChange={(e) => this.setState({ to_date: e.target.value })}
              style={inp}
            />
          </div>
          <div>
            <label style={lbl}>Sold To (filter)</label>
            <input
              value={sold_to}
              onChange={(e) => this.setState({ sold_to: e.target.value })}
              style={{ ...inp, width: 180 }}
              placeholder="Agent or client name"
            />
          </div>
          <button
            onClick={this.load}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "9px 18px",
              borderRadius: 10,
              border: "none",
              background: "#16213E",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <RefreshCw size={13} /> Run Report
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #E5E7EB",
              background: "#FAFBFF",
              overflowX: "auto",
            }}
          >
            {TAB.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => this.setState({ tab: key })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "12px 20px",
                  border: "none",
                  background: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  color: tab === key ? "#16213E" : "#9CA3AF",
                  borderBottom:
                    tab === key ? "2px solid #16213E" : "2px solid transparent",
                  whiteSpace: "nowrap",
                }}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
          <div style={{ padding: 20 }}>{this.renderContent()}</div>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

export default ReportsPage;